import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

import { getDynamicMenus, DynamicMenu } from '@/api';
import { getPublicSettings } from '@/api/settings';

export type AdminTab = 'dashboard' | 'solutions' | 'tools' | 'members' | 'upload' | 'settings' | 'admins' | 'review' | 'pageeditor' | 'auditlog' | 'points' | 'announcement' | 'contact' | 'dict' | 'menus';

interface AdminSidebarProps {
  activeTab: AdminTab;
  onTabChange: (tab: AdminTab) => void;
  reviewBadge?: number;
}

function NavButton({
  item,
  active,
  onClick,
  badge,
}: {
  item: { key: AdminTab; label: string; icon: string };
  active: boolean;
  onClick: () => void;
  badge?: number;
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap mb-0.5 ${
        active ? 'bg-[#0ea5e9] text-white' : 'text-gray-400 hover:text-white hover:bg-white/5'
      }`}
    >
      <i className={`${item.icon} text-base`}></i>
      <span className="flex-1 text-left">{item.label}</span>
      {badge && badge > 0 ? (
        <span className="w-5 h-5 flex items-center justify-center bg-[#f97316] text-white text-xs rounded-full">
          {badge}
        </span>
      ) : null}
    </button>
  );
}

export default function AdminSidebar({ activeTab, onTabChange, reviewBadge = 0 }: AdminSidebarProps) {
  const [menus, setMenus] = useState<DynamicMenu[]>([]);

  const loadMenus = () => {
    getDynamicMenus()
      .then((res) => {
        setMenus(res || []);
      })
      .catch((err) => {
        console.error('Failed to load dynamic menus:', err);
      });
  };

  useEffect(() => {
    loadMenus();
    window.addEventListener('wanxiang_menus_updated', loadMenus);
    return () => window.removeEventListener('wanxiang_menus_updated', loadMenus);
  }, []);

  const [siteName, setSiteName] = useState('万象杂货铺');
  const [logoPreview, setLogoPreview] = useState('');

  useEffect(() => {
    getPublicSettings().then(res => {
      if ((res as any).siteName) setSiteName((res as any).siteName);
      if ((res as any).logoPreview) setLogoPreview((res as any).logoPreview);
    }).catch(err => console.error(err));
  }, []);

  const mainMenus = menus.filter((m) => m.group === 'main');
  const contentMenus = menus.filter((m) => m.group === 'content');
  const systemMenus = menus.filter((m) => m.group === 'system');
  const adminMenus = menus.filter((m) => m.group === 'admin');

  return (
    <div className="w-56 bg-[#0f172a] flex flex-col shrink-0 h-screen">
      <div className="p-5 border-b border-white/10">
        <Link to="/" className="flex items-center gap-2 cursor-pointer">
          <div className="w-8 h-8 flex items-center justify-center bg-gradient-to-br from-[#0ea5e9] to-[#0284c7] rounded-lg overflow-hidden shrink-0">
            {logoPreview ? (
              <img src={logoPreview} alt="logo" className="w-full h-full object-cover" />
            ) : (
              <i className="ri-store-2-line text-white text-sm"></i>
            )}
          </div>
          <div>
            <div className="text-white font-bold text-sm truncate max-w-[120px]">{siteName}</div>
            <div className="text-white/40 text-xs">管理后台</div>
          </div>
        </Link>
      </div>

      <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
        {mainMenus.length > 0 && (
          <div className="text-white/30 text-xs font-semibold px-3 py-2 mt-1">主要功能</div>
        )}
        {mainMenus.map((item) => (
          <NavButton
            key={item.key}
            item={{ key: item.key as AdminTab, label: item.label, icon: item.icon }}
            active={activeTab === item.key}
            onClick={() => onTabChange(item.key as AdminTab)}
          />
        ))}

        {contentMenus.length > 0 && (
          <div className="text-white/30 text-xs font-semibold px-3 py-2 mt-2">内容管理</div>
        )}
        {contentMenus.map((item) => (
          <NavButton
            key={item.key}
            item={{ key: item.key as AdminTab, label: item.label, icon: item.icon }}
            active={activeTab === item.key}
            onClick={() => onTabChange(item.key as AdminTab)}
          />
        ))}

        {systemMenus.length > 0 && (
          <div className="text-white/30 text-xs font-semibold px-3 py-2 mt-2">系统</div>
        )}
        {systemMenus.map((item) => (
          <NavButton
            key={item.key}
            item={{ key: item.key as AdminTab, label: item.label, icon: item.icon }}
            active={activeTab === item.key}
            onClick={() => onTabChange(item.key as AdminTab)}
          />
        ))}

        {adminMenus.length > 0 && (
          <div className="text-white/30 text-xs font-semibold px-3 py-2 mt-2">权限管理</div>
        )}
        {adminMenus.map((item) => (
          <NavButton
            key={item.key}
            item={{ key: item.key as AdminTab, label: item.label, icon: item.icon }}
            active={activeTab === item.key}
            onClick={() => onTabChange(item.key as AdminTab)}
            badge={item.key === 'review' ? reviewBadge : undefined}
          />
        ))}
      </nav>

      <div className="p-3 border-t border-white/10 space-y-1">
        <Link
          to="/"
          className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-white/60 hover:text-white hover:bg-white/5 transition-all cursor-pointer"
        >
          <i className="ri-home-line text-base"></i>
          返回前台
        </Link>
        <Link
          to="/login"
          className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-white/40 hover:text-red-400 hover:bg-white/5 transition-all cursor-pointer"
        >
          <i className="ri-logout-box-line text-base"></i>
          退出登录
        </Link>
      </div>
    </div>
  );
}
